import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendCompany,
	FrontendProductOrder,
	ShippingInfo,
	BillingInfo,
	FrontendOrderItem,
	FrontendEngineerInfo,
} from "node-library/dist/portal";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import {
	DiscountUnit,
	Severity,
	TaxOptions,
} from "node-library/dist/constants";
import {
	HANDLING_DEFAULT_DESCRIPTION,
	SHIPPING_DEFAULT_DESCRIPTION,
} from "node-library/dist/description";
import { ModalWithButtons } from "../../common-components/modals/modal";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	YellowCrossIcon,
	YellowWarnIcon,
} from "../../common-components/toast/warnIcons";
import { GreenTickIcon } from "../../common-components/toast/successIcons";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../common-components/toast/errorIcons";
import { LongLoadingAnimation } from "../../common-components/animation/LongLoadingAnimation";
import DiscardProductConfirmModal from "../../common-components/modals/discardProductConfirmModal";
import Lottie from "react-lottie";
import * as OrderSubmittedAnimationJSON from "../../images/animations/lotties/order_submitted.json";
import {
	AddClientIcon,
	AddItemBagIcon,
	ReviewOrderEyeIcon,
} from "../../common-components/svgIcons";
import { StepsBar } from "../../common-components/steps-bar";
import OrderClientDetail from "./newOrder/orderClientDetail";
import OrderAddItems from "./newOrder/orderAddItems";
import NewItem from "./newSample/newItem";
import ShippingHandlingFee from "./newSample/shippingHandingFee";
import OrderReview from "./newOrder/orderReview";

interface Props {
	user: FrontendUserInfo | undefined;
	company: FrontendCompany | undefined;
	order?: FrontendProductOrder;
	orderItemList?: Array<FrontendOrderItem>;
	gotoViewOrderPage: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;

	isInAdminView?: boolean;
	goBackToOrderDetailsPage?: () => void;
}

export default function NewOrEditOrder(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const user = props.user;
	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);

	const [usersList, setUsersList] = useState<Array<FrontendUserInfo>>([]);

	const [selectedUser, setSeletedUser] = useState<FrontendUserInfo | null>(
		null
	);

	// const [engineersList, setEngineersList] = useState<Array<FrontendUserInfo>>(
	// 	[]
	// );

	const [loading, setLoading] = useState<boolean>(false);

	const [orderItemList, setOrderItemList] = useState<FrontendOrderItem[]>(
		props.orderItemList || []
	);

	const [handlingFee, setHandlingFee] = useState<number>(
		props.order?.handlingFee || 0
	);
	const [shippingFee, setShippingFee] = useState<number>(
		props.order?.shippingFee || 0
	);
	const [handlingDescription, setHandlingDescription] = useState<string>(
		HANDLING_DEFAULT_DESCRIPTION
	);
	const [shippingDescription, setShippingDescription] = useState<string>(
		props.order?.shippingDescription || SHIPPING_DEFAULT_DESCRIPTION
	);
	const [fullServiceShippingSolution, setFullServiceShippingSolution] =
		useState<boolean>(props.order?.fullServiceShippingSolution || false);
	const [discount, setDiscount] = useState<number>(props.order?.discount || 0);
	const [discountUnit, setDiscountUnit] = useState<DiscountUnit>(
		props.order?.discountUnit || DiscountUnit.NUMBER
	);
	const [tax, setTax] = useState<number>(props.order?.tax || 0);
	const [taxOption, setTaxOption] = useState<TaxOptions>(TaxOptions.ZERO);
	const [currency, setCurrency] = useState<string>(
		props.order?.currency || "CAD"
	);
	const [total, setTotal] = useState<number>(props.order?.totalPrice || 0);
	const [subtotal, setSubtotal] = useState<number>(0);

	const [itemTotal, setItemTotal] = useState<number>(0);

	const [updatingItem, setUpdatingItem] = useState<FrontendOrderItem | null>(
		null
	);

	const [submittedModalOpen, setSubmittedModalOpen] = useState<boolean>(false);

	//how do we include this when editing order?
	// const [billingInfoEmails, setBillingInfoEmails] = useState<Array<string>>(
	// 	props.order?.billingInfoCopyEmails ||
	// 		user?.defaultBillingInfoCopyEmails ||
	// 		[]
	// );

	//billing information

	const [billingInfo, setBillingInfo] = useState<BillingInfo>({
		name: props.order?.billingName || selectedUser?.defaultBillingName || "",
		company:
			props.order?.billingCompany || selectedUser?.defaultBillingCompany || "",
		address:
			props.order?.billingAddress || selectedUser?.defaultBillingAddress || "",
		city: props.order?.billingCity || selectedUser?.defaultBillingCity || "",
		postalCode:
			props.order?.billingPostalCode ||
			selectedUser?.defaultBillingPostalCode ||
			"",
		province:
			props.order?.billingProvince ||
			selectedUser?.defaultBillingProvince ||
			"",
		country:
			props.order?.billingCountry || selectedUser?.defaultBillingCountry || "",
		phoneNumber:
			props.order?.billingPhoneNumber ||
			selectedUser?.defaultBillingPhoneNumber ||
			"",
		email: props.order?.billingEmail || selectedUser?.defaultBillingEmail || "",
	});

	//shipping info

	const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
		name: props.order?.shippingName || selectedUser?.defaultShippingName || "",
		address:
			props.order?.shippingAddress ||
			selectedUser?.defaultShippingAddress ||
			"",
		city: props.order?.shippingCity || selectedUser?.defaultShippingCity || "",
		postalCode:
			props.order?.shippingPostalCode ||
			selectedUser?.defaultShippingPostalCode ||
			"",
		province:
			props.order?.shippingProvince ||
			selectedUser?.defaultShippingProvince ||
			"",
		country:
			props.order?.shippingCountry ||
			selectedUser?.defaultShippingCountry ||
			"",
		phoneNumber:
			props.order?.shippingPhoneNumber ||
			selectedUser?.defaultShippingPhoneNumber ||
			"",
	});

	const [saveInfoAsDefault, setSaveInfoAsDefault] = useState<boolean>(false);
	const [submittingOrder, setSubmittingOrder] = useState<boolean>(false);

	const [discardProductConfirmOpen, setDiscardProductConfirmOpen] =
		useState<boolean>(false);

	const [quoteNumber, setQuoteNumber] = useState<string>(
		props.order?.quoteId || ""
	);
	const [quoteNumberErrorMsg, setQuoteNumberErrorMsg] = useState<string>("");

	const [userListLoading, setUserListLoading] = useState<boolean>(false);

	// new
	function addItem(newItem: FrontendOrderItem) {
		setOrderItemList((prevItems) => [...prevItems, newItem]);
		setUnsavedChanges(true);
	}

	function deleteItem(orderItemId: string) {
		setOrderItemList((prevItems) =>
			prevItems.filter((item) => item.orderItemId !== orderItemId)
		);
		openSnackbar(
			translation("ItemDeleted"),
			Severity.ERROR,
			undefined,
			<RedTrashIcon />
		);
		setUnsavedChanges(true);
	}
	function deleteHandlingFee() {
		setHandlingFee(0);
		openSnackbar(
			translation("HandlingFeeRemoved"),
			Severity.ERROR,
			undefined,
			<RedTrashIcon />
		);
		setUnsavedChanges(true);
	}
	function deleteShippingFee() {
		setShippingFee(0);
		setFullServiceShippingSolution(false);
		setShippingDescription(SHIPPING_DEFAULT_DESCRIPTION);
		openSnackbar(
			translation("ShippingFeeRemoved"),
			Severity.ERROR,
			undefined,
			<RedTrashIcon />
		);
		setUnsavedChanges(true);
	}
	function updateItem(itemToUpdate: FrontendOrderItem) {
		setOrderItemList((prevItems) =>
			prevItems.map((item) =>
				item.orderItemId === itemToUpdate.orderItemId ? itemToUpdate : item
			)
		);
		setUnsavedChanges(true);
	}

	function discardItem() {
		if (updatingItem) {
			goBackToAddItemPage();
		} else {
			setDiscardProductConfirmOpen(true);
		}
	}
	//
	function goBackToAddItemPage() {
		setUpdatingItem(null);
		setCurrentPageIndex(2);
	}
	function goToAddItemPage() {
		setCurrentPageIndex(2);
	}
	function goBackToClientDetail() {
		setCurrentPageIndex(1);
	}

	//
	function goToUpdateItem(item: FrontendOrderItem) {
		setUpdatingItem(item);
		setCurrentPageIndex(3);
	}

	function goToAddNewItem() {
		if (orderItemList.length >= 70) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("ItemLimitReachedDescription"),
				<YellowWarnIcon />
			);
			return;
		}
		setUpdatingItem(null);
		setCurrentPageIndex(3);
	}
	//

	function goToAddShippingHandling() {
		setCurrentPageIndex(4);
	}

	function goToReviewOrder() {
		setCurrentPageIndex(5);
	}

	async function saveOrderAndEditLater() {
		await submitOrder(true);
	}

	function gotoViewOrderPage() {
		setSubmittedModalOpen(false);
		props.gotoViewOrderPage();
	}

	function getStepBarIndex(currentPageIndex: number): number {
		return currentPageIndex === 1 ? 0 : currentPageIndex === 5 ? 2 : 1;
	}
	async function discardOrder() {
		if (props.order) {
			//send a request to delete the temp order in the backend
			try {
				let response;
				if (props.isInAdminView) {
					response = await sendPOSTRequest("/orders/delete-order", {
						orderId: props.order.orderId,
					});
				} else {
					response = await sendPOSTRequest("/orders/delete-unstarted-order", {
						orderId: props.order.orderId,
					});
				}
				//go to the submitted order page
				openSnackbar(
					translation("OrderDiscardSuccess"),
					Severity.WARN,
					undefined,
					<YellowCrossIcon />
				);
				setUnsavedChanges(false);
				setTimeout(() => {
					props.gotoViewOrderPage();
				}, 500);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />
				);
			}
		} else {
			//new order, just clear out the items
			setOrderItemList([]);
			setUnsavedChanges(false);
			openSnackbar(
				translation("OrderDiscardSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />
			);
			setTimeout(() => {
				props.gotoViewOrderPage();
			}, 500);
		}
	}
	async function submitOrder(saveAndEditLater: boolean) {
		setSubmittingOrder(true);
		setSubmittedModalOpen(true);

		try {
			let response;
			if (props.isInAdminView) {
				response = await sendPOSTRequest("/orders/admin-edit-order", {
					itemList: orderItemList,
					orderId: props.order ? props.order.orderId : undefined,
					frontendOrderUser: selectedUser,
					saveInfoAsDefault,
					billingInfo: {
						name: billingInfo.name.trim(),
						company: billingInfo.company.trim(),
						address: billingInfo.address.trim(),
						city: billingInfo.city.trim(),
						province: billingInfo.province.trim(),
						country: billingInfo.country.trim(),
						postalCode: billingInfo.postalCode.trim(),
						phoneNumber: billingInfo.phoneNumber.trim(),
						email: billingInfo.email.trim(),
					} as BillingInfo,
					shippingInfo: {
						name: shippingInfo.name.trim(),
						address: shippingInfo.address.trim(),
						city: shippingInfo.city.trim(),
						province: shippingInfo.province.trim(),
						country: shippingInfo.country.trim(),
						postalCode: shippingInfo.postalCode.trim(),
						phoneNumber: shippingInfo.phoneNumber.trim(),
					} as ShippingInfo,
					handlingFee,
					shippingFee,
					shippingDescription,
					fullServiceShippingSolution,
					discount,
					discountUnit,
					tax,
					taxOption,
					currency,
					itemTotal,
					totalPrice: total,
					quoteId: quoteNumber,
					// billingName,
					// billingAddress,
					// billingCity: city,
					// billingProvince: province,
					// billingCountry: country,
					// billingPostalCode: postalCode,
					// billingPhoneNumber: phoneNumber,
					// billingEmail,
					// testReportCopyEmails: testReportEmails,
					// billingInfoCopyEmails: billingInfoEmails,
					orderComment: undefined,
				});
			} else {
				response = await sendPOSTRequest("/orders/submit-order", {
					itemList: orderItemList,
					completeOrder: !saveAndEditLater,
					incompleteOrderId: props.order ? props.order.orderId : undefined,
					frontendOrderUser: selectedUser,
					saveInfoAsDefault,
					billingInfo: {
						name: billingInfo.name.trim(),
						company: billingInfo.company.trim(),
						address: billingInfo.address.trim(),
						city: billingInfo.city.trim(),
						province: billingInfo.province.trim(),
						country: billingInfo.country.trim(),
						postalCode: billingInfo.postalCode.trim(),
						phoneNumber: billingInfo.phoneNumber.trim(),
						email: billingInfo.email.trim(),
					} as BillingInfo,
					shippingInfo: {
						name: shippingInfo.name.trim(),
						address: shippingInfo.address.trim(),
						city: shippingInfo.city.trim(),
						province: shippingInfo.province.trim(),
						country: shippingInfo.country.trim(),
						postalCode: shippingInfo.postalCode.trim(),
						phoneNumber: shippingInfo.phoneNumber.trim(),
					} as ShippingInfo,
					handlingFee,
					shippingFee,
					shippingDescription,
					fullServiceShippingSolution,
					discount,
					discountUnit,
					tax,
					taxOption,
					currency,
					itemTotal,
					totalPrice: total,
					quoteId: quoteNumber,
					// billingName,
					// billingAddress,
					// billingCity: city,
					// billingProvince: province,
					// billingCountry: country,
					// billingPostalCode: postalCode,
					// billingPhoneNumber: phoneNumber,
					// billingEmail,
					// billingInfoCopyEmails: billingInfoEmails,
					orderComment: undefined,
				});
			}
			setUnsavedChanges(false);
			//go to the submitted order page

			if (saveAndEditLater) {
				const successMessage: string = translation("OrderSavedSuccessfully");
				openSnackbar(
					successMessage,
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />
				);
				setSubmittedModalOpen(false);

				setTimeout(() => {
					props.gotoViewOrderPage();
				}, 500);
			} else {
				if (props.isInAdminView) {
					openSnackbar(
						translation("OrderDetailsUpdated"),
						Severity.SUCCESS,
						undefined,
						<GreenTickIcon />
					);
					setSubmittedModalOpen(false);

					props.gotoViewOrderPage();
				}
			}
		} catch (error: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
			setSubmittedModalOpen(false);

			goBackToAddItemPage();
		} finally {
			setSubmittingOrder(false);
		}
	}

	async function getUserList() {
		try {
			setUserListLoading(true);
			const response = await sendPOSTRequest("/users/order-get-users", {});

			const users: Array<FrontendUserInfo> = response?.data?.users;
			setUsersList(users);
			setSeletedUser(
				users.find((user) => user.userName === props.order?.userName) || null
			);
			setUserListLoading(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}
	// async function getEngineerList() {
	// 	try {
	// 		const response = await sendPOSTRequest("/users/order-get-engineers", {});

	// 		const engineers: Array<FrontendEngineerInfo> = response?.data?.engineers;
	// 		setEngineersList(engineers);
	// 		// setSelectedEngineer(
	// 		// 	engineers.find(
	// 		// 		(engineer) => engineer.userId === props.order?.engineerUsername
	// 		// 	) || null
	// 		// );
	// 	} catch (error: any) {
	// 		console.error(error);
	// 		openSnackbar(
	// 			translation("SystemError"),
	// 			Severity.ERROR,
	// 			translation("SystemErrorMsg"),
	// 			<RedErrorIcon />
	// 		);
	// 	}
	// }

	useEffect(() => {
		getUserList();
	}, [props.order]);

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 1:
			currentComponent = (
				<OrderClientDetail
					selectedUser={selectedUser}
					setSelectedUser={setSeletedUser}
					order={props.order}
					usersList={usersList}
					quoteNumber={quoteNumber}
					setQuoteNumber={setQuoteNumber}
					quoteNumberErrorMsg={quoteNumberErrorMsg}
					setQuoteNumberErrorMsg={setQuoteNumberErrorMsg}
					billingInfo={billingInfo}
					setBillingInfo={setBillingInfo}
					shippingInfo={shippingInfo}
					setShippingInfo={setShippingInfo}
					saveInfoAsDefault={saveInfoAsDefault}
					setSaveInfoAsDefault={setSaveInfoAsDefault}
					userListLoading={userListLoading}
					setUnsavedChanges={setUnsavedChanges}
					goToAddItemPage={goToAddItemPage}
					isInAdminView={props.isInAdminView}
					goBackToOrderDetailsPage={props.goBackToOrderDetailsPage}
				/>
			);
			break;
		case 2:
			currentComponent = (
				<OrderAddItems
					licenseLoading={loading}
					isInAdminView={props.isInAdminView}
					goBackToOrderDetailsPage={props.goBackToOrderDetailsPage}
					goBackToClientDetail={goBackToClientDetail}
					itemList={orderItemList}
					deleteItem={deleteItem}
					goToUpdateItem={goToUpdateItem}
					goToAddNewItem={goToAddNewItem}
					goToReviewOrder={goToReviewOrder}
					goToAddShippingHandling={goToAddShippingHandling}
					discardOrder={discardOrder}
					saveOrderAndEditLater={saveOrderAndEditLater}
					handlingFee={handlingFee}
					shippingFee={shippingFee}
					handlingDescription={handlingDescription}
					shippingDescription={shippingDescription}
					fullServiceShippingSolution={fullServiceShippingSolution}
					deleteHandlingFee={deleteHandlingFee}
					deleteShippingFee={deleteShippingFee}
					discount={discount}
					setDiscount={setDiscount}
					discountUnit={discountUnit}
					setDiscountUnit={setDiscountUnit}
					tax={tax}
					setTax={setTax}
					taxOption={taxOption}
					setTaxOption={setTaxOption}
					currency={currency}
					setCurrency={setCurrency}
					total={total}
					setTotal={setTotal}
					subtotal={subtotal}
					setSubtotal={setSubtotal}
					itemTotal={itemTotal}
					setItemTotal={setItemTotal}
					quoteNumber={quoteNumber}
					setUnsavedChanges={setUnsavedChanges}
				/>
			);
			break;
		case 3:
			currentComponent = (
				<NewItem
					addItem={addItem}
					currency={currency}
					updateItem={updateItem}
					discardItem={discardItem}
					isInAdminView={props.isInAdminView}
					itemToUpdate={updatingItem}
					goBackToAddItemPage={goBackToAddItemPage}
					setUnsavedChanges={setUnsavedChanges}
				></NewItem>
			);
			break;
		case 4:
			currentComponent = (
				<ShippingHandlingFee
					currency={currency}
					handlingFee={handlingFee}
					shippingFee={shippingFee}
					setHandlingFee={setHandlingFee}
					setShippingFee={setShippingFee}
					shippingDescription={shippingDescription}
					setShippingDescription={setShippingDescription}
					handlingDescription={handlingDescription}
					setHandlingDescription={setHandlingDescription}
					fullServiceShippingSolution={fullServiceShippingSolution}
					setFullServiceShippingSolution={setFullServiceShippingSolution}
					goBackToAddItemPage={goBackToAddItemPage}
					setUnsavedChanges={setUnsavedChanges}
				/>
			);
			break;
		case 5:
			currentComponent = (
				<OrderReview
					selectedUser={selectedUser as FrontendUserInfo}
					billingInfo={billingInfo}
					shippingInfo={shippingInfo}
					licenseLoading={loading}
					isInAdminView={props.isInAdminView}
					goBackToOrderDetailsPage={props.goBackToOrderDetailsPage}
					goBackToClientDetail={goBackToClientDetail}
					goBackToAddItemPage={goBackToAddItemPage}
					itemList={orderItemList}
					deleteItem={deleteItem}
					goToUpdateItem={goToUpdateItem}
					goToAddNewItem={goToAddNewItem}
					goToSubmitOrder={submitOrder}
					goToAddShippingHandling={goToAddShippingHandling}
					discardOrder={discardOrder}
					saveOrderAndEditLater={saveOrderAndEditLater}
					handlingFee={handlingFee}
					shippingFee={shippingFee}
					handlingDescription={handlingDescription}
					shippingDescription={shippingDescription}
					fullServiceShippingSolution={fullServiceShippingSolution}
					deleteHandlingFee={deleteHandlingFee}
					deleteShippingFee={deleteShippingFee}
					discount={discount}
					discountUnit={discountUnit}
					tax={tax}
					currency={currency}
					total={total}
					subtotal={subtotal}
					itemTotal={itemTotal}
					quoteNumber={quoteNumber}
					setUnsavedChanges={setUnsavedChanges}
				/>
			);
			break;
	}

	const orderSubmittedModal: React.ReactNode = (
		<ModalWithButtons
			headerText={""}
			open={submittedModalOpen}
			hideHeader={true}
			modalWidth={500}
			setOpen={(open) => {
				gotoViewOrderPage();
			}}
			buttons={
				submittingOrder
					? []
					: [
							// <OutlinedButton
							// 	key="modal-goto-view-orders-button"
							// 	id="modal-goto-view-orders-button"
							// 	children={translation("Vieworder")}
							// 	onClick={gotoViewOrderPage}
							// 	outlineColor={NeutralColor.NEUTRAL_GREY_900}
							// ></OutlinedButton>,
							// <BlueButton
							// 	key="modal-download-forms-button"
							// 	id="modal-download-forms-button"
							// 	children={translation("DownloadCoCform")}
							// 	onClick={() => {
							// 		window.open(cocURL, "_blank");
							// 	}}
							// ></BlueButton>,
					  ]
			}
		>
			{submittingOrder ? (
				<div>
					<div style={{ display: "flex", justifyContent: "center" }}>
						<LongLoadingAnimation></LongLoadingAnimation>
					</div>
					<div
						className="center-text"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "5px",
						}}
					>
						<div
							className="typography-body-01 typography-bold"
							style={{ margin: "10px 0px" }}
						>
							{`${translation("SubmittingOrder")}`}
						</div>
						<div className="typography-caption">
							{translation("SubmittingOrderDescription")}
						</div>
						{/* {containsSameDayTests && (
						<div
							className="typography-caption"
							style={{
								backgroundColor: SemanticColor.WARNING_100,
								padding: "2px 6px",
								borderRadius: "4px",
							}}
						>
							{translation("SameDayContactText")}
						</div>
					)} */}
					</div>
				</div>
			) : (
				<div>
					<div className="confetti">
						<Lottie
							options={{
								loop: false,
								autoplay: true,
								animationData: OrderSubmittedAnimationJSON,
								rendererSettings: {
									preserveAspectRatio: "xMidYMid slice", // Aspect ratio settings
								},
							}}
						/>
					</div>
					<div
						className="center-text"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "5px",
						}}
					>
						<div
							className="typography-heading-03 typography-bold"
							style={{ margin: "10px 0px" }}
						>
							{`${translation("OrderSubmitted")}!`}
						</div>
						<div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
						>
							{translation("OrderSubmittedDescriptionText")}
						</div>
						<div
							className="typography-caption"
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "20px",
								margin: "20px 0px",
								position:
									"relative" /* Parent container to help position the vertical line */,
							}}
						>
							<div className="order-submitted-action-row">
								<div className="order-submitted-action-number">
									<div>{1}</div>
								</div>
								<div>
									<span className="typography-bold">
										{translation("SubmittedStepOneBoldText")}
									</span>
									<span> {translation("SubmittedStepOneText")}</span>
								</div>
							</div>
							<div className="order-submitted-action-row">
								<div className="order-submitted-action-number">
									<div>{2}</div>
								</div>
								<div>
									<span className="typography-bold">
										{translation("SubmittedStepTwoBoldText")}
									</span>
									<span> {translation("SubmittedStepTwoText")}</span>
								</div>
							</div>
							<div className="order-submitted-action-row">
								<div className="order-submitted-action-number">
									<div>{3}</div>
								</div>
								<div>
									<span className="typography-bold">
										{translation("SubmittedStepThreeBoldText")}
									</span>
									<span> {translation("SubmittedStepThreeText")}</span>
									<div
										style={{
											backgroundColor: SecondaryColor.SECONDARY_100,
											borderRadius: "4px",
											padding: "4px 8px",
											marginTop: "4px",
										}}
									>
										{props.company?.address}
									</div>
								</div>
							</div>
							<div className="order-submitted-action-row">
								<div className="order-submitted-action-number">
									<div>{4}</div>
								</div>
								<div>
									<span className="typography-bold">
										{translation("SubmittedStepFourBoldText")}
									</span>
									<span> {translation("SubmittedStepFourText")}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</ModalWithButtons>
	);

	const stepBarTexts = [
		translation("ClientDetail"),
		translation("AddItemToOrder"),
		translation("ReviewOrder"),
	];

	const stepBarIcon = [
		<AddClientIcon fillColor="currentColor"></AddClientIcon>,
		<AddItemBagIcon strokeColor="currentColor"></AddItemBagIcon>,
		<ReviewOrderEyeIcon strokeColor="currentColor"></ReviewOrderEyeIcon>,
	];
	const newOrderStepProgressBar: React.ReactNode = (
		<StepsBar
			items={stepBarTexts}
			icons={stepBarIcon}
			currentItem={getStepBarIndex(currentPageIndex)}
			setCurrentItem={setCurrentPageIndex}
		/>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			{newOrderStepProgressBar}
			{currentComponent}
			{orderSubmittedModal}
			<DiscardProductConfirmModal
				open={discardProductConfirmOpen}
				setOpen={setDiscardProductConfirmOpen}
				proceedAction={() => {
					goBackToAddItemPage();
				}}
			></DiscardProductConfirmModal>
		</div>
	);
}
